/**
 * Proximity Engines 6amico — due canali tecnologici:
 * 1) Bluetooth Beacon (porti) → stato "Utente Sbarcato"
 * 2) Geofencing GPS (entroterra) → notifiche contestuali
 */

export const PROXIMITY_STATE = {
  IDLE: "idle",
  SBARCATO: "utente_sbarcato",
  IN_GEOFENCE: "in_geofence",
};

export const GEOFENCE_CATEGORY = {
  MANGIARE: "mangiare",
  DORMIRE: "dormire",
  SAGRE: "sagre",
};

const EARTH_RADIUS_M = 6_371_000;

/** Distanza in metri tra due coordinate WGS84. */
export function distanceMeters(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== "number" || Number.isNaN(v))) {
    return Infinity;
  }
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

export function normalizeBeaconUuid(uuid) {
  return (uuid || "").trim().toUpperCase();
}

/** Trova il porto associato a un beacon UUID. */
export function resolvePortByBeaconUuid(uuid, ports = []) {
  const key = normalizeBeaconUuid(uuid);
  if (!key) return null;
  return (
    ports.find((p) => normalizeBeaconUuid(p.beacon_uuid || p.beaconUuid) === key) ||
    null
  );
}

/** Zone geofence attive con raggio (default 500 m). */
export function activeGeofenceZones(zones = [], defaultRadiusM = 500) {
  return (zones || [])
    .filter((z) => z && z.active !== false)
    .map((z) => ({
      ...z,
      radiusM: typeof z.radiusM === "number" ? z.radiusM : defaultRadiusM,
    }));
}

/** Utente dentro il recinto virtuale? */
export function isInsideGeofence(position, zone) {
  if (!position || !zone) return false;
  const d = distanceMeters(position.lat, position.lng, zone.lat, zone.lng);
  return d <= (zone.radiusM ?? 500);
}

/**
 * Controlla tutte le zone; ritorna le nuove entrate (non già in `enteredIds`).
 */
export function checkGeofenceEntries(position, zones, enteredIds = new Set()) {
  const entered = [];
  if (!position) return entered;
  for (const zone of activeGeofenceZones(zones)) {
    if (enteredIds.has(zone.id)) continue;
    if (isInsideGeofence(position, zone)) {
      entered.push(zone);
    }
  }
  return entered;
}

/** Testo notifica push contestuale. */
export function buildGeofenceNotification(zone) {
  const name = zone.name || "questo luogo";
  const templates = {
    [GEOFENCE_CATEGORY.MANGIARE]: `Sei vicino a ${name}, vuoi prenotare un tavolo per stasera?`,
    [GEOFENCE_CATEGORY.DORMIRE]: `Sei vicino a ${name}, vuoi verificare disponibilità per stanze?`,
    [GEOFENCE_CATEGORY.SAGRE]: `Sei vicino a ${name}, vuoi vedere il programma della sagra?`,
  };
  const body =
    zone.pushTemplate ||
    templates[zone.category] ||
    `Sei nell'area Cicerone di ${name}. Apri la scheda su 6amico.`;
  return {
    title: "6amico · Area Cicerone",
    body,
    href: zone.href || (zone.comuneSlug ? `/comune/${zone.comuneSlug}` : "/esplora-la-mappa"),
    zoneId: zone.id,
    category: zone.category,
  };
}

/**
 * Beacon rilevato → stato Utente Sbarcato + comuni amici in primo piano.
 */
export function handleBeaconDetected({
  uuid,
  ports = [],
  comuniCatalog = [],
  defaultComuniAmiciSlugs = [],
}) {
  const port = resolvePortByBeaconUuid(uuid, ports);
  if (!port) {
    return {
      ok: false,
      state: PROXIMITY_STATE.IDLE,
      error: "beacon_unknown",
      uuid: normalizeBeaconUuid(uuid),
    };
  }

  const slugList =
    port.comuniAmiciSlugs ||
    port.comuni_amici_slugs ||
    defaultComuniAmiciSlugs ||
    [];

  const bySlug = Object.fromEntries(
    (comuniCatalog || []).map((c) => [c.slug || c.id?.replace?.("comune-", ""), c]),
  );

  const comuniAmici = slugList
    .map((slug) => {
      const key = slug.replace(/^comune-/, "");
      const fromCatalog = bySlug[key] || bySlug[slug];
      if (fromCatalog) return fromCatalog;
      return {
        slug: key,
        name: key,
        href: `/comune/${key}`,
      };
    })
    .filter(Boolean);

  return {
    ok: true,
    state: PROXIMITY_STATE.SBARCATO,
    uuid: normalizeBeaconUuid(uuid),
    port,
    comuniAmici,
    message: `Utente Sbarcato · ${port.name || port.subtitle || "Porto"}`,
    activatedAt: new Date().toISOString(),
  };
}

/**
 * Simula la ricezione di un segnale iBeacon/BLE (dev e test in assenza hardware).
 */
export function simulateBeaconScan(uuid, options = {}) {
  return handleBeaconDetected({
    uuid,
    ports: options.ports || [],
    comuniCatalog: options.comuniCatalog || [],
    defaultComuniAmiciSlugs: options.defaultComuniAmiciSlugs || [],
  });
}

/** Notifica di sistema + callback opzionale (es. toast UI). */
export async function dispatchProximityNotification(payload, { onToast } = {}) {
  const { title, body, href } = payload;
  if (typeof onToast === "function") {
    onToast({ title, description: body, href });
  }
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { delivered: false, channel: "toast_only" };
  }
  try {
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
    if (Notification.permission === "granted") {
      const n = new Notification(title, {
        body,
        icon: "/favicon.svg",
        tag: payload.zoneId || payload.portSlug || "6amico-proximity",
        data: { href },
      });
      n.onclick = () => {
        window.focus();
        if (href) window.location.href = href;
      };
      return { delivered: true, channel: "push" };
    }
  } catch {
    /* permesso negato o contesto non sicuro */
  }
  return { delivered: false, channel: "denied" };
}

/**
 * Watcher GPS per geofencing (usa watchPosition; fallback simulazione).
 */
export function createGpsGeofenceWatcher({
  zones = [],
  enteredIds = new Set(),
  onEnter,
  onPosition,
  onError,
  intervalMs = 12_000,
  simulatePosition = null,
}) {
  const entered = new Set(enteredIds);
  let watchId = null;
  let simTimer = null;

  const tick = (position) => {
    if (typeof onPosition === "function") onPosition(position);
    const hits = checkGeofenceEntries(position, zones, entered);
    for (const zone of hits) {
      entered.add(zone.id);
      if (typeof onEnter === "function") {
        onEnter(zone, buildGeofenceNotification(zone));
      }
    }
  };

  if (simulatePosition && typeof simulatePosition.lat === "number") {
    tick(simulatePosition);
    simTimer = window.setInterval(() => tick(simulatePosition), intervalMs);
    return () => {
      if (simTimer) window.clearInterval(simTimer);
    };
  }

  if (typeof navigator !== "undefined" && navigator.geolocation) {
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        tick({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      (err) => {
        if (typeof onError === "function") onError(err);
      },
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 15_000 },
    );
    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }

  if (typeof onError === "function") {
    onError(new Error("Geolocalizzazione non disponibile"));
  }
  return () => {};
}

/**
 * Aloni "Area Cicerone Attivo" per la mappa (porti, borghi, POI geofence).
 */
export function getCiceroneHalos({
  punti = [],
  geofenceZones = [],
  defaults = {},
}) {
  const portRadius = defaults.portBeaconRadiusM ?? 2500;
  const borgoRadius = defaults.borgoComuneRadiusM ?? 8000;
  const poiRadius = defaults.radiusM ?? 500;

  const halos = [];

  for (const p of punti) {
    if (p.active === false || typeof p.lat !== "number") continue;
    if (p.channel === "mare" && p.kind === "porto") {
      halos.push({
        id: `cicerone-port-${p.id}`,
        lat: p.lat,
        lng: p.lng,
        radiusM: p.ciceroneRadiusM ?? portRadius,
        kind: "porto",
        channel: "mare",
        label: p.name,
      });
    } else if (p.channel === "terra" && p.kind === "comune") {
      halos.push({
        id: `cicerone-borgo-${p.id}`,
        lat: p.lat,
        lng: p.lng,
        radiusM: p.ciceroneRadiusM ?? borgoRadius,
        kind: "comune",
        channel: "terra",
        label: p.name,
      });
    }
  }

  for (const z of activeGeofenceZones(geofenceZones, poiRadius)) {
    halos.push({
      id: `cicerone-poi-${z.id}`,
      lat: z.lat,
      lng: z.lng,
      radiusM: z.radiusM,
      kind: z.category || "poi",
      channel: "terra",
      label: z.name,
    });
  }

  return halos;
}

/** Estrae porti con beacon da puntiMappa o API. */
export function portsWithBeaconFromPunti(punti = []) {
  return punti
    .filter((p) => p.channel === "mare" && p.kind === "porto")
    .map((p) => ({
      slug: p.id,
      name: p.name,
      subtitle: p.subtitle,
      lat: p.lat,
      lon: p.lng,
      lng: p.lng,
      beacon_uuid: p.beacon_uuid || p.beaconUuid,
      comuniAmiciSlugs: p.comuniAmiciSlugs || [],
      is_hub: Boolean(p.isHub),
      href: p.href,
    }));
}

/** Raggio default geofence porto turistico (demo 500 m). */
export const PORTO_GEOFENCE_DEFAULT_RADIUS_M = 500;

/**
 * Porto turistico più vicino e stato geofence (Inter-Mappa Mare-Terra).
 * @param {{ lat: number, lng: number }} position
 * @param {Array<{ id: string, coords?: { lat: number, lng: number }, lat?: number, lng?: number, geofenceRadiusM?: number }>} porti
 * @param {number} [defaultRadiusM]
 */
export function resolveNearestPorto(position, porti = [], defaultRadiusM = PORTO_GEOFENCE_DEFAULT_RADIUS_M) {
  if (!position || !porti?.length) {
    return {
      porto: null,
      nearestPorto: null,
      distanzaMetri: Infinity,
      inside: false,
    };
  }
  let nearest = null;
  let bestD = Infinity;
  for (const p of porti) {
    const lat = p.coords?.lat ?? p.lat;
    const lng = p.coords?.lng ?? p.lng;
    if (typeof lat !== "number" || typeof lng !== "number") continue;
    const d = distanceMeters(position.lat, position.lng, lat, lng);
    if (d < bestD) {
      bestD = d;
      nearest = p;
    }
  }
  if (!nearest) {
    return { porto: null, nearestPorto: null, distanzaMetri: Infinity, inside: false };
  }
  const radiusM = nearest.geofenceRadiusM ?? defaultRadiusM;
  const inside = bestD <= radiusM;
  return {
    porto: inside ? nearest : null,
    nearestPorto: nearest,
    distanzaMetri: bestD,
    inside,
    radiusM,
  };
}

/** Catalogo comuni amici da punti terra. */
export function comuniCatalogFromPunti(punti = []) {
  return punti
    .filter((p) => p.channel === "terra" && p.kind === "comune")
    .map((p) => ({
      slug: (p.id || "").replace(/^comune-/, ""),
      name: p.name,
      subtitle: p.subtitle,
      href: p.href || `/comune/${(p.id || "").replace(/^comune-/, "")}`,
      lat: p.lat,
      lng: p.lng,
    }));
}
